import {
  useMutation,
  UseMutationOptions,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryOptions,
  UseQueryResult,
} from "@tanstack/react-query";
import { API_ENDPOINTS } from "../../../constants/api_endpoints";
import { IWebInfoPayload, WebInfoType } from "../../../types/api/webInfo";
import { TResponse, TResponseWithQData } from "../../../types/common";
import {getWebInfoDetail, getWebInfoList,saveWebInfo } from "./callbacks";

export const useWebInfoSave = (
  {
    onSuccess,
    onError,
  }: {
    onSuccess?: (res: TResponse<IWebInfoPayload>) => void;
    onError?: (error: unknown) => void;
  },
  options: UseMutationOptions<
    TResponse<IWebInfoPayload>,
    unknown,
    IWebInfoPayload
  > = {}
): UseMutationResult<TResponse<IWebInfoPayload>, unknown, IWebInfoPayload> => {

  return useMutation<TResponse<IWebInfoPayload>, unknown, IWebInfoPayload>({
    mutationFn: async (data: IWebInfoPayload) => {
      const response = await saveWebInfo(data);
      return response.data;
    },
    onSuccess: (res: any, ...rest) => {
 
      onSuccess?.(res);
      options?.onSuccess?.(res, ...rest);
    },
    onError: (error, ...rest) => {
      onError?.(error);
      options?.onError?.(error, ...rest);
    },
    ...options,
  });
};

export const useWebInfoList = (
  options?: Omit<
    UseQueryOptions<TResponse<IWebInfoPayload[]>, Error>,
    "queryKey" | "queryFn"
  >
): UseQueryResult<TResponse<IWebInfoPayload[]>, Error> => {
  return useQuery<TResponse<IWebInfoPayload[]>, Error>({
    queryKey: [API_ENDPOINTS.WEB_INFO_LIST],
    queryFn: async () => {
      const res = await getWebInfoList();
      return res.data;
    },
    select: (res) => ({
      ...res,
      data: res.data?.map((x) => ({ key: x._id, ...x })) || [],
    }),
    staleTime: 300000,
    refetchOnWindowFocus: false,
    ...options,
  });
};

export const useWebInfoDetail = (
  type: WebInfoType,
  options?: Omit<
    UseQueryOptions<TResponse<IWebInfoPayload>, Error>,
    "queryKey" | "queryFn"
  >
): UseQueryResult<TResponse<IWebInfoPayload>, Error> => {
  return useQuery<TResponse<IWebInfoPayload>, Error>({
    queryKey: [API_ENDPOINTS.WEB_INFO_DETAIL, type],
    queryFn: async () => {
      const res = await getWebInfoDetail(type);
      return res.data;
    },
    enabled: !!type,
    refetchOnWindowFocus: false,
    staleTime: 300000,
    ...options,
  });
};
